import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { rupiah, tgl, today } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Penjualan() {
  const [available, setAvailable] = useState([]);
  const [sales, setSales] = useState([]);
  const [form, setForm] = useState({ item_id: "", harga_jual: "", pembeli: "", nama_sales: "", metode: "tunai", tanggal: today(), catatan: "" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/items", { params: { status: "tersedia" } }).then((r) => setAvailable(r.data)).catch(() => {});
    api.get("/sales").then((r) => setSales(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selected = available.find((i) => i.id === form.item_id);

  const pickItem = (id) => {
    const item = available.find((i) => i.id === id);
    setForm((f) => ({ ...f, item_id: id, harga_jual: item?.harga_jual_estimasi ? String(item.harga_jual_estimasi) : "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/sales", { ...form, harga_jual: Number(form.harga_jual) });
      toast.success("Penjualan tercatat");
      setForm({ item_id: "", harga_jual: "", pembeli: "", nama_sales: "", metode: "tunai", tanggal: today(), catatan: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal mencatat penjualan"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="penjualan-page">
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Barang Keluar</p>
      <h1 className="mt-1 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Penjualan Barang</h1>

      <form onSubmit={submit} className="mt-5 rounded-md border border-border bg-white p-5 space-y-4" data-testid="penjualan-form">
        <div className="space-y-1.5">
          <Label>Pilih Barang (Stok Tersedia)</Label>
          <Select value={form.item_id} onValueChange={pickItem} required>
            <SelectTrigger data-testid="penjualan-item-select"><SelectValue placeholder="Pilih barang..." /></SelectTrigger>
            <SelectContent>
              {available.map((i) => <SelectItem key={i.id} value={i.id}>{i.kode} — {i.nama} · Rencana {rupiah(i.harga_jual_estimasi)}</SelectItem>)}
            </SelectContent>
          </Select>
          {selected && (
            <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-xs space-y-0.5" data-testid="penjualan-item-info">
              <p className="text-muted-foreground">Harga beli: <b className="text-foreground">{rupiah(selected.harga_beli)}</b> · Kondisi: {selected.kondisi}</p>
              <p className="text-muted-foreground">Harga jual direncanakan: <b className="text-primary">{rupiah(selected.harga_jual_estimasi)}</b></p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Harga Jual Aktual (Rp)</Label>
            <Input required type="number" min="0" value={form.harga_jual} onChange={(e) => set("harga_jual", e.target.value)} data-testid="penjualan-harga-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Tanggal Jual</Label>
            <Input type="date" value={form.tanggal} onChange={(e) => set("tanggal", e.target.value)} data-testid="penjualan-tanggal-input" />
          </div>
        </div>
        {selected && form.harga_jual && (
          <p className="text-sm" data-testid="penjualan-laba-preview">
            Perkiraan laba: <span className={`font-bold ${Number(form.harga_jual) - selected.harga_beli >= 0 ? "text-emerald-600" : "text-red-600"}`}>{rupiah(Number(form.harga_jual) - selected.harga_beli)}</span>
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Nama Pembeli</Label>
            <Input value={form.pembeli} onChange={(e) => set("pembeli", e.target.value)} placeholder="Opsional" data-testid="penjualan-pembeli-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Nama Sales</Label>
            <Input value={form.nama_sales} onChange={(e) => set("nama_sales", e.target.value)} placeholder="Opsional" data-testid="penjualan-sales-input" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Pembayaran</Label>
          <div className="grid grid-cols-2 gap-2">
            {[["tunai", "Tunai (Kas)"], ["transfer", "Transfer Bank"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("metode", v)} data-testid={`penjualan-metode-${v}`}
                className={`rounded-md border px-3 py-2 text-sm font-semibold transition-colors duration-150 active:scale-[0.98] ${form.metode === v ? "border-primary bg-primary text-white" : "border-border bg-white"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <Button type="submit" className="w-full rounded-full active:scale-95 transition-transform duration-100" disabled={loading || !form.item_id} data-testid="penjualan-submit-button">
          {loading ? "Menyimpan..." : "Catat Penjualan"}
        </Button>
      </form>

      <h2 className="mt-8 text-base font-bold tracking-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>Riwayat Penjualan</h2>
      <div className="mt-3 rounded-md border border-border bg-white divide-y divide-border" data-testid="penjualan-list">
        {sales.length === 0 && <p className="p-4 text-sm text-muted-foreground">Belum ada penjualan.</p>}
        {sales.map((s) => (
          <div key={s.id} className="p-3.5" data-testid={`penjualan-item-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{s.nama} <span className="text-xs font-normal text-muted-foreground">({s.kode})</span></p>
                <p className="text-xs text-muted-foreground">{tgl(s.tanggal)}{s.pembeli ? ` · ${s.pembeli}` : ""}{s.nama_sales ? ` · Sales: ${s.nama_sales}` : ""} · {s.metode === "transfer" ? "Transfer Bank" : "Tunai"}</p>
              </div>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-sm">
              {s.harga_jual_rencana > 0 && <span className="text-muted-foreground">Rencana: <b className="text-foreground">{rupiah(s.harga_jual_rencana)}</b></span>}
              <span>Aktual: <b>{rupiah(s.harga_jual)}</b></span>
              <span className={s.harga_jual - s.harga_beli >= 0 ? "text-emerald-600" : "text-red-600"}>Laba: <b>{rupiah(s.harga_jual - s.harga_beli)}</b></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
